<template>
  <div :class="['base-form code-group', { 'code-group--error': errorMessage }]">
    <div class="code-group__inputs">
      <span
        v-for="(item, index) in Array(codeLength)"
        :key="index"
        :class="['code-group__input-wrapper', { 'code-group__input-wrapper--loading': isLoading }]"
      >
        <input
          :ref="setFieldRef"
          class="base-input base-input--black"
          autocomplete="off"
          type="tel"
          maxlength="1"
          @focusout="setCode"
          @input="onInput(index, $event)"
          @keydown="onKeydown(index, $event)"
          @keypress="onKeypress"
          @paste="onPaste(index, $event)"
        />

        <div
          v-if="isLoading"
          class="code-group__preloader"
        >
          <Preloader
            :is-multiple="false"
            :index-circle="index"
          />
        </div>
      </span>
    </div>

    <div class="input-error">
      <template v-if="errorMessage">
        {{ errorMessage }}
      </template>
    </div>
  </div>
</template>

<script lang="ts">
import { Preloader } from '@/shared/ui/preloader';

const setFocusAtEnd = (elem: { [x: string]: any }) => {
  const elemLen = elem.value.length;

  if (elem.selectionStart || elem.selectionStart === 0) {
    elem.selectionStart = elemLen + 1;
    elem.selectionEnd = elemLen + 1;

    elem.focus();
  }
};

export default defineComponent({
  components: {
    Preloader,
  },

  props: {
    codeLength: {
      type: Number,
      default: 4,
    },
    errorMessage: {
      type: String,
      default: null,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['setCode'],

  setup() {
    let fields = [] as HTMLInputElement[];

    const setFieldRef = (el: Element | ComponentPublicInstance | null) => {
      fields.push(el as HTMLInputElement);
    };

    onBeforeUpdate(() => {
      fields = [];
    });

    return {
      fields,
      setFieldRef,
    };
  },

  data() {
    return {
      code: [] as string[],
    };
  },

  methods: {
    onInput(index: number, event: Event) {
      event.stopPropagation();

      if (!this.fields[index]!.value) {
        return;
      }

      this.code[index] = this.fields[index]!.value;

      this.focusNextOrBlurCurrent(index);
    },

    onKeypress(event: KeyboardEvent) {
      event.stopPropagation();

      const keyCode = event.which || event.keyCode;

      if (!(
        this.isNumberKeyCode(keyCode) ||
        this.isTab(keyCode) ||
        this.isBackspace(keyCode) ||
        this.isMetaKey(event, keyCode)
      )) {
        event.preventDefault();
      }
    },

    onKeydown(index: number, event: KeyboardEvent) {
      event.stopPropagation();

      const keyCode = event.which || event.keyCode;

      if (this.isBackspace(keyCode)) {
        this.backspaceHandler(index, event);
      }

      if (this.isLeftArrow(keyCode) || this.isRightArrow(keyCode)) {
        this.arrowHandler(index, keyCode, event);
      }
    },

    onPaste(index: number, event: ClipboardEvent) {
      event.preventDefault();
      // @ts-ignore
      let pasteData = (event.clipboardData || window.clipboardData).getData('text');

      pasteData = pasteData
        .replace(/\s/g, '')
        .substr(0, this.codeLength - index)
        .split('');

      this.pasteData(index, pasteData);
    },

    setCode() {
      if (this.code.length !== this.codeLength) {
        return;
      }

      this.$emit('setCode', this.code.join(''));
    },

    backspaceHandler(index: number, event: Event) {
      this.fields[index]!.value = '';

      if (index > 0) {
        setFocusAtEnd(this.fields[index - 1]!);
      }

      event.preventDefault();
    },

    arrowHandler(index: number, keyCode: number, event: Event) {
      const newIndex = this.isLeftArrow(keyCode) ? index - 1 : index + 1;

      if (newIndex < 0 || newIndex >= this.codeLength) {
        return;
      }

      setFocusAtEnd(this.fields[newIndex]!);

      event.preventDefault();
    },

    focusNextOrBlurCurrent(index: number) {
      if (index !== this.codeLength - 1) {
        setFocusAtEnd(this.fields[index + 1]!);

        return;
      }

      this.fields[index]!.blur();
    },

    pasteData(index: number, pasteData: string) {
      for (let i = 0; i < this.fields.length; i += 1) {
        const numericValue = Number(pasteData[i]);

        if (Number.isNaN(numericValue)) {
          return;
        }

        this.fields[i + index]!.value = pasteData[i]!;
        this.code[i + index] = pasteData[i]!;
      }

      if (pasteData.length + index < this.codeLength) {
        setFocusAtEnd(this.fields[pasteData.length]!);
      } else {
        this.fields[index]!.blur();
      }
    },

    isNumberKeyCode(keyCode: number) {
      return keyCode >= 48 && keyCode <= 57;
    },

    isTab(keyCode: number) {
      return keyCode === 9;
    },

    isBackspace(keyCode: number) {
      return keyCode === 8;
    },

    isMetaKey(e: KeyboardEvent, keyCode: number) {
      return e.metaKey && keyCode === 118;
    },

    isLeftArrow(keyCode: number) {
      return keyCode === 37;
    },

    isRightArrow(keyCode: number) {
      return keyCode === 39;
    },
  },
});
</script>

<style lang="scss" scoped>
.code-group {
  width: auto;

  &--error {
    input {
      border-color: #ff4646 !important;
    }

    &::after {
      display: none;
    }
  }

  &__inputs {
    display: flex;
    justify-content: center;
  }

  &__input-wrapper {
    position: relative;

    &--loading {
      input {
        color: #d8d8d8;
        cursor: default;
      }
    }

    input {
      padding: 8px;
      text-align: center;
    }

    &:not(:last-of-type) {
      margin-right: 16px;
    }
  }

  &__preloader {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    position: absolute;
    top: 0;
    left: 50%;
    transform: translateX(-7px);
  }
}
</style>
